import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, TreePine, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Navigation({ scrolled }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const mainNavLinks = [
    { name: "Services", href: isHomePage ? "#services" : "/#services", hasDropdown: true },
    { name: "Fire Safety", href: isHomePage ? "#fire-safety" : "/#fire-safety" },
    { name: "About", href: isHomePage ? "#about" : "/#about" },
    { name: "Service Areas", href: isHomePage ? "#service-areas" : "/#service-areas" },
    { name: "Pricing", href: isHomePage ? "#pricing" : "/#pricing" },
    { name: "Tree Care", href: "/tree-care-guide" },
    { name: "Contact", href: isHomePage ? "#contact" : "/#contact" },
  ];

  const servicePages = [
    { name: "Golf Courses", href: "/golf-courses" },
    { name: "Hotels & Resorts", href: "/hotels-resorts" },
    { name: "Residential", href: "/residential" },
  ];

  return (
    <>
      {/* Emergency Banner - Fixed at very top */}
      <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#DC2626] text-white py-2 text-center text-sm z-[60]">
        <span className="font-bold">24/7 Emergency Response</span>
        <span className="mx-3 text-white/50">|</span>
        <span>Fire Safety & Storm Damage Experts</span>
      </div>

      <nav
        className={`fixed top-[36px] left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "nav-scrolled py-3"
            : "bg-transparent py-5"
        }`}
        data-testid="navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Premium Logo */}
            <Link to="/" className="logo-container" data-testid="logo">
              <div className="logo-icon">
                <TreePine className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div className="logo-text-wrapper">
                <p className="logo-main">
                  CELEBRITY <span className="highlight">TREE</span>
                </p>
                <p className="logo-sub">Service LLC • Arizona</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {mainNavLinks.map((link) => (
                <div key={link.name} className="relative">
                  {link.hasDropdown ? (
                    <div 
                      className="relative"
                      onMouseEnter={() => setServicesDropdown(true)}
                      onMouseLeave={() => setServicesDropdown(false)}
                    >
                      <a
                        href={link.href}
                        className="flex items-center gap-1 text-sm font-medium tracking-wide text-white/80 hover:text-[#10B981] transition-colors duration-300"
                      >
                        {link.name}
                        <ChevronDown className="w-3 h-3" />
                      </a>
                      
                      {/* Dropdown Menu */}
                      {servicesDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-48 bg-[#0A1628] border border-[#10B981]/20 shadow-xl">
                          {servicePages.map((page) => (
                            <Link
                              key={page.name}
                              to={page.href}
                              className="block px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-[#10B981]/20 transition-colors"
                            >
                              {page.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-sm font-medium tracking-wide text-white/80 hover:text-[#10B981] transition-colors duration-300"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-sm font-medium tracking-wide text-white/80 hover:text-[#10B981] transition-colors duration-300"
                    >
                      {link.name}
                    </a>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-5">
              <Button
                asChild
                className="bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white rounded-none px-6 py-5 uppercase tracking-[0.15em] text-xs font-bold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all duration-300"
                data-testid="nav-cta-button"
              >
                <a href="#contact">Free Estimate</a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-7 h-7 text-white" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-nav-overlay ${mobileOpen ? "open" : ""}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Mobile Menu */}
      <div
        className={`mobile-nav ${mobileOpen ? "open" : ""}`}
        data-testid="mobile-nav"
      >
        <div className="flex justify-between items-center mb-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
              <TreePine className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-['Cormorant_Garamond'] text-lg font-bold text-white">
                CELEBRITY <span className="text-[#10B981]">TREE</span>
              </p>
            </div>
          </div>
          <button 
            onClick={() => setMobileOpen(false)}
            className="p-2 hover:bg-white/10 transition-colors"
            data-testid="mobile-nav-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        <div className="flex flex-col gap-1">
          {mainNavLinks.map((link) => (
            link.isRoute ? (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-lg font-medium text-white/80 hover:text-[#10B981] py-3 border-b border-white/10 transition-colors"
              >
                {link.name}
              </Link>
            ) : (
            <a
              key={link.name}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="text-lg font-medium text-white/80 hover:text-[#10B981] py-3 border-b border-white/10 transition-colors"
            >
              {link.name}
            </a>
            )
          ))}
          
          {/* Service Pages Section */}
          <div className="pt-4 pb-2">
            <p className="text-[#D4A853] text-xs font-['Cinzel'] tracking-[0.2em] uppercase mb-3">
              Service Pages
            </p>
            {servicePages.map((page) => (
              <Link
                key={page.name}
                to={page.href}
                onClick={() => setMobileOpen(false)}
                className="block text-base font-medium text-white/70 hover:text-[#10B981] py-2 pl-4 border-l-2 border-[#10B981]/30 transition-colors"
              >
                {page.name}
              </Link>
            ))}
          </div>

          {/* Careers Link */}
          <div className="pt-2">
            <Link
              to="/careers"
              onClick={() => setMobileOpen(false)}
              className="block text-lg font-medium text-[#D4A853] hover:text-[#10B981] py-3 border-b border-white/10 transition-colors"
              data-testid="mobile-careers-link"
            >
              Careers — Join Our Team
            </Link>
          </div>
        </div>

        <div className="mt-10 space-y-4">
          <a
            href="tel:4803517775"
            className="flex items-center gap-3 text-white font-bold text-lg"
          >
            <div className="w-10 h-10 bg-[#10B981] flex items-center justify-center">
              <Phone className="w-5 h-5" />
            </div>
            (480) 351-7775
          </a>
          <Button
            asChild
            className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none py-4 uppercase tracking-[0.15em] text-sm font-bold"
          >
            <a href="#contact" onClick={() => setMobileOpen(false)}>
              Get Free Estimate
            </a>
          </Button>
        </div>

        {/* Trust Badge */}
        <div className="mt-10 p-4 border border-[#10B981]/30 bg-[#10B981]/5">
          <p className="text-[#D4A853] text-xs font-['Cinzel'] tracking-[0.2em] uppercase mb-1">
            Certified Excellence
          </p>
          <p className="text-white text-sm">ISA Certified Arborists</p>
          <p className="text-white/60 text-xs">Serving Arizona Since 2015</p>
        </div>
      </div>
    </>
  );
}
